import Storage from 'react-native-storage';
import { utilities } from '@weatherbug/common';

const { PulseApi } = utilities;

const _getLegalPoliciesKey = 'legalPolicies';
const _getLegalPoliciesUri='/api/legal/policies/v1';

const _getComplianceRequirementsV3Key = 'compliancev3';
const _getComplianceRequirementsV3Uri = '/api/legal/compliance/v3';

const _getCurrentLegalPoliciesKey = 'currentPolicies';
const _getCurrentLegalPoliciesUri = '/api/legal/policies/v1/current';

export class LegalProvider {
    constructor(baseUrl, authId, secret, options = { timeout: 5000 }) {
        this.options = options;

        this.pulseApi = new PulseApi(
            authId,
            secret,
            baseUrl,
            this.options.timeout
        );

        if(!window.storage) {
            window.storage  = new Storage({
                // maximum capacity, default 1000
                size: 1000,
                // Use AsyncStorage for RN, or window.localStorage for web. If not set, data would be lost after reload.
                storageBackend: window.localStorage,
                // expire time, default 30 minutes
                defaultExpires: 1000 * 900,
                // cache data in the memory. default is true.
                enableCache: true
            });
        }

        window.storage.sync[_getLegalPoliciesKey] = this.legalPoliciesSyncer;
        window.storage.sync[_getCurrentLegalPoliciesKey] = this.getCurrentLegalPoliciesSyncer;
        window.storage.sync[_getComplianceRequirementsV3Key] = this.getComplianceRequirementsV3Syncer;
    }

    async legalPoliciesSyncer(params) {
        const { syncParams } = params;
        const { options, pulseApi, hmac, key, id, queryParams } = syncParams;
        const { resolve, reject } = params;

        let apiParams = {
            ...queryParams.stdParameters,
            countrycode: queryParams.countrycode,
            languagecode: queryParams.languagecode,
            tou: queryParams.touVersion || '', //These parameters are required so we have to do an empty string if they aren't set.
            pp: queryParams.ppVersion || '',
            verbose: queryParams.verbose
        };
        try {
            const response = await pulseApi.getData(_getLegalPoliciesUri, apiParams, options.timeout, 'json', hmac);
            if (!response || !(response.r || response.result)) {
                reject('Invalid response');
            }

            const result = response.r || response.result;
            result.responseTime = Date.now();

            window.storage.save({
                key,
                id,
                data: result
            });

            resolve(result);
        } catch(error) {
            reject(error);
        }
    }

    async getLegalPolicies(touVersion, ppVersion, countrycode, languagecode, stdParameters) {
        let storage = window.storage;
        let pulseApi = this.pulseApi;
        let options = this.options;

        const params = {
            touVersion,
            ppVersion,
            countrycode,
            languagecode,
            stdParameters,
            verbose: true
        };

        const id = JSON.stringify(
            {
                countrycode: params.countrycode.replace('_', ''),
                languagecode: params.languagecode.replace('_', ''),
                touVersion: params.touVersion.replace('_', ''),
                ppVersion: params.ppVersion.replace('_', '')
            });

        return await storage.load({
            key: _getLegalPoliciesKey,
            id,
            autoSync: true,
            syncInBackground: false,
            syncParams: {
                pulseApi,
                options,
                hmac: true,
                key: _getLegalPoliciesKey,
                id,
                queryParams: params
            }
        });
    }

    async getCurrentLegalPoliciesSyncer(params) {
        const { syncParams } = params;
        const { options, pulseApi, hmac, key, id, queryParams } = syncParams;
        const { resolve, reject } = params;

        let apiParams = {
            ...queryParams.stdParameters,
            countrycode: queryParams.countrycode,
            languagecode: queryParams.languagecode,
            verbose: queryParams.verbose
        };

        try {
            const response = await pulseApi.getData(_getCurrentLegalPoliciesUri, apiParams, options.timeout, 'json', hmac);
            if (!response || !(response.r || response.result)) {
                reject('Invalid response');
            }

            const result = response.r || response.result;
            result.responseTime = Date.now();

            window.storage.save({
                key,
                id,
                data: result
            });

            resolve(result);
        } catch(error) {
            reject(error);
        }
    }

    async getCurrentLegalPolicies(countrycode, languagecode, stdParameters) {
        let storage = window.storage;
        let pulseApi = this.pulseApi;
        let options = this.options;

        const params = {
            countrycode,
            languagecode,
            stdParameters,
            verbose: true
        };

        const id = JSON.stringify(
            {
                countrycode: params.countrycode.replace('_', ''),
                languagecode: params.languagecode.replace('_', '')
            });

        const cacheKey = _getCurrentLegalPoliciesKey;

        return await storage.load({
            key: cacheKey,
            id,
            autoSync: true,
            syncInBackground: false,
            syncParams: {
                pulseApi,
                options,
                hmac: true,
                key: cacheKey,
                id,
                queryParams: params
            }
        });
    }

    async getComplianceRequirementsV3Syncer(params) {
        const { syncParams } = params;
        const { options, pulseApi, hmac, key, id, queryParams } = syncParams;
        const { resolve, reject } = params;
        const locationType = queryParams.ipAddress && 'IPAddress';

        let apiParams = {
            ...queryParams.stdParameters,
            verbose: queryParams.verbose
        };

        if (queryParams.ipAddress && locationType) {
            apiParams.location = queryParams.ipAddress;
            apiParams.locationType = locationType;
        }

        try {
            const response = await pulseApi.getData(_getComplianceRequirementsV3Uri, apiParams, options.timeout, 'json', hmac);
            if (!response || !(response.r || response.result)) {
                reject('Invalid response');
            }

            const result = response.r || response.result;
            result.responseTime = Date.now();

            window.storage.save({
                key,
                data: result
            });

            resolve(result);
        } catch(error) {
            reject(error);
        }
    }

    async getComplianceRequirementsV3(stdParameters, ipAddress) {
        let storage = window.storage;
        let pulseApi = this.pulseApi;
        let options = this.options;

        const params = {
            stdParameters,
            verbose: true,
            ipAddress
        };

        return await storage.load({
            key: _getComplianceRequirementsV3Key,
            autoSync: true,
            syncInBackground: false,
            syncParams: {
                pulseApi,
                options,
                hmac: true,
                key: _getComplianceRequirementsV3Key,
                queryParams: params
            }
        });
    }

}
